import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { env } from '@/config/env';
import { supabase } from '@/lib/supabase';
import ReactECharts from 'echarts-for-react';
import { User, X, Youtube } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';

export interface ChartModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  artist: string;
}

interface ChartData {
  broadcast_date: string;
  count: number;
}

const ChartModal: React.FC<ChartModalProps> = ({ isOpen, onClose, title, artist }) => {
  const [chartData, setChartData] = React.useState<ChartData[]>([]);
  const [artistChartData, setArtistChartData] = React.useState<ChartData[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [artistImage, setArtistImage] = useState<string | null>(null);
  const [imageLoading, setImageLoading] = useState(true);
  const [imageError, setImageError] = useState(false);
  const chartRef = useRef<ReactECharts>(null);
  const artistChartRef = useRef<ReactECharts>(null);

  useEffect(() => {
    const fetchAlbumCover = async () => {
      if (!isOpen || !artist || !title) return;

      setImageLoading(true);
      setImageError(false);
      setArtistImage(null);

      try {
        const apiKey = env.GOOGLE_API_KEY;
        const cseId = env.GOOGLE_CSE_ID;

        if (!apiKey || !cseId) {
          console.error('Google API credentials are missing');
          setImageError(true);
          setImageLoading(false);
          return;
        }

        // 앨범 커버 검색 쿼리 (우선순위 높음)
        const albumSearchQueries = [
          `${artist} ${title} album cover`,
          `${artist} ${title} cd cover`,
          `${artist} ${title} artwork`,
          `${artist} ${title} single cover`,
          `${artist} ${title} music cover`,
          `${title} album cover`,
          `${title} cd cover`,
          `${artist} ${title} cover`,
          `${artist} ${title}`,
          `${title} by ${artist} cover`,
          `${title} by ${artist} album`,
        ];

        // 가수 사진 검색 쿼리 (앨범 커버를 찾지 못했을 때 사용)
        const artistSearchQueries = [
          `${artist} official photo`,
          `${artist} profile picture`,
          `${artist} singer photo`,
          `${artist} artist photo`,
          `${artist} musician photo`,
          `${artist} portrait`,
          `${artist} photo`,
          `${artist} picture`,
          `${artist} singer`,
          `${artist} artist`,
          `${artist} musician`,
          `${artist} singer official`,
          `${artist} artist official`,
          `${artist} singer profile`,
          `${artist} artist profile`,
          `${artist} singer portrait`,
          `${artist} artist portrait`,
          `${artist} official`,
          `${artist} profile`,
          `${artist} portrait`,
          `${artist} singer official photo`,
          `${artist} artist official photo`,
          `${artist} singer profile picture`,
          `${artist} artist profile picture`,
        ];

        let foundImage = null;

        // 앨범 커버 검색 시도
        for (const searchQuery of albumSearchQueries) {
          const apiUrl = `https://www.googleapis.com/customsearch/v1?q=${encodeURIComponent(searchQuery)}&cx=${cseId}&key=${apiKey}&searchType=image&imgSize=medium&imgType=photo&safe=active`;

          const response = await fetch(apiUrl, {
            headers: {
              Accept: 'application/json',
            },
          });

          if (!response.ok) continue;

          const data = await response.json();

          if (data.items && data.items.length > 0) {
            // 첫 번째 이미지부터 순차적으로 검사
            for (const img of data.items) {
              const title = img.title.toLowerCase();
              const artist_lower = artist.toLowerCase();
              const song_lower = title.toLowerCase();

              // 크기 조건 완화
              const isGoodSize = img.image?.width >= 150 && img.image?.height >= 150;

              // 가수 이름이나 곡명 중 하나만 포함되어도 허용
              const hasArtistName =
                title.includes(artist_lower) ||
                artist_lower.includes(title) ||
                title.split(' ').some((word: string) => artist_lower.includes(word)) ||
                artist_lower.split(' ').some((word: string) => title.includes(word));
              const hasSongName =
                title.includes(song_lower) ||
                song_lower.includes(title) ||
                title.split(' ').some((word: string) => song_lower.includes(word)) ||
                song_lower.split(' ').some((word: string) => title.includes(word));

              // 앨범 커버 관련 키워드 확인 (조건 완화)
              const isAlbumCover =
                title.includes('album') ||
                title.includes('cover') ||
                title.includes('artwork') ||
                title.includes('cd') ||
                title.includes('single') ||
                title.includes('music') ||
                title.includes('song') ||
                title.includes('track');

              if (isGoodSize && (hasArtistName || hasSongName) && isAlbumCover) {
                foundImage = img.link;
                break;
              }
            }

            if (foundImage) break;
          }
        }

        // 앨범 커버를 찾지 못한 경우 가수 사진 검색
        if (!foundImage) {
          for (const searchQuery of artistSearchQueries) {
            const apiUrl = `https://www.googleapis.com/customsearch/v1?q=${encodeURIComponent(searchQuery)}&cx=${cseId}&key=${apiKey}&searchType=image&imgSize=medium&imgType=photo&safe=active`;

            const response = await fetch(apiUrl, {
              headers: {
                Accept: 'application/json',
              },
            });

            if (!response.ok) continue;

            const data = await response.json();

            if (data.items && data.items.length > 0) {
              // 첫 번째 이미지부터 순차적으로 검사
              for (const img of data.items) {
                const title = img.title.toLowerCase();
                const artist_lower = artist.toLowerCase();

                // 크기 조건 완화
                const isGoodSize = img.image?.width >= 150 && img.image?.height >= 150;

                // 가수 이름 포함 여부 확인 (부분 매치 허용)
                const hasArtistName =
                  title.includes(artist_lower) ||
                  artist_lower.includes(title) ||
                  title.split(' ').some((word: string) => artist_lower.includes(word)) ||
                  artist_lower.split(' ').some((word: string) => title.includes(word));

                // 불필요한 이미지 제외 조건 완화
                const isNotUnwanted =
                  !title.includes('fan') &&
                  !title.includes('concert') &&
                  !title.includes('group') &&
                  !title.includes('band') &&
                  !title.includes('logo') &&
                  !title.includes('icon') &&
                  !title.includes('poster') &&
                  !title.includes('banner');

                if (isGoodSize && hasArtistName && isNotUnwanted) {
                  foundImage = img.link;
                  break;
                }
              }

              if (foundImage) break;
            }
          }
        }

        if (foundImage) {
          const validateImage = async (url: string) => {
            try {
              const response = await fetch(url, { method: 'HEAD' });
              return response.ok;
            } catch (error) {
              console.error('Image validation error:', error);
              return false;
            }
          };

          if (await validateImage(foundImage)) {
            const img = new Image();
            img.src = foundImage;
            img.onload = () => {
              setArtistImage(foundImage);
              setImageLoading(false);
            };
            img.onerror = () => {
              setImageError(true);
              setImageLoading(false);
            };
          } else {
            setImageError(true);
            setImageLoading(false);
          }
        } else {
          setImageError(true);
          setImageLoading(false);
        }
      } catch (error) {
        console.error('Error in fetchAlbumCover:', error);
        setImageError(true);
        setImageLoading(false);
      }
    };

    fetchAlbumCover();
  }, [isOpen, artist, title]);

  useEffect(() => {
    const fetchChartData = async () => {
      if (!isOpen || !title || !artist) return;

      try {
        setLoading(true);
        setError(null);

        // 곡 데이터 조회
        const { data: songData, error: songError } = await supabase
          .from('music_data')
          .select('broadcast_date')
          .ilike('title', title)
          .ilike('artist', artist)
          .order('broadcast_date', { ascending: true });

        // 가수 데이터 조회
        const { data: artistData, error: artistError } = await supabase
          .from('music_data')
          .select('broadcast_date')
          .ilike('artist', artist)
          .order('broadcast_date', { ascending: true });

        if (songError || artistError) {
          console.error('Supabase query error:', songError || artistError);
          throw new Error(
            `데이터 조회 중 오류가 발생했습니다: ${(songError || artistError)?.message}`
          );
        }

        if (!songData || !artistData) {
          throw new Error('데이터가 없습니다.');
        }

        // 곡 데이터 집계
        const songAggregatedData = songData.reduce((acc: { [key: string]: number }, item) => {
          if (!item.broadcast_date) return acc;
          const date = new Date(item.broadcast_date);
          const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
          acc[monthKey] = (acc[monthKey] || 0) + 1;
          return acc;
        }, {});

        // 가수 데이터 집계
        const artistAggregatedData = artistData.reduce((acc: { [key: string]: number }, item) => {
          if (!item.broadcast_date) return acc;
          const date = new Date(item.broadcast_date);
          const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
          acc[monthKey] = (acc[monthKey] || 0) + 1;
          return acc;
        }, {});

        // 곡 차트 데이터 변환
        const songChartDataArray = Object.entries(songAggregatedData)
          .map(([date, count]) => ({
            broadcast_date: date,
            count,
          }))
          .sort((a, b) => a.broadcast_date.localeCompare(b.broadcast_date));

        // 가수 차트 데이터 변환
        const artistChartDataArray = Object.entries(artistAggregatedData)
          .map(([date, count]) => ({
            broadcast_date: date,
            count,
          }))
          .sort((a, b) => a.broadcast_date.localeCompare(b.broadcast_date));

        if (songChartDataArray.length === 0 && artistChartDataArray.length === 0) {
          throw new Error('차트 데이터를 생성할 수 없습니다.');
        }

        setChartData(songChartDataArray);
        setArtistChartData(artistChartDataArray);
      } catch (error) {
        console.error('Error in fetchChartData:', error);
        setError(
          error instanceof Error ? error.message : '데이터를 불러오는 중 오류가 발생했습니다.'
        );
        setChartData([]);
        setArtistChartData([]);
      } finally {
        setLoading(false);
      }
    };

    if (isOpen) {
      fetchChartData();
    }
  }, [isOpen, title, artist]);

  const chartOption = {
    animation: true,
    backgroundColor: 'transparent',
    tooltip: {
      trigger: 'axis',
      formatter: (params: unknown) => {
        if (
          Array.isArray(params) &&
          params.length > 0 &&
          typeof params[0] === 'object' &&
          params[0] !== null
        ) {
          const param = params[0] as { axisValue?: string; data?: number };
          const date = param.axisValue || '';
          const count = param.data || 0;
          return `${date}<br/>재생 횟수: ${count}회`;
        }
        return '';
      },
      backgroundColor: 'rgba(255, 255, 255, 0.9)',
      borderColor: '#ddd',
      borderWidth: 1,
      textStyle: {
        color: '#333',
      },
    },
    grid: {
      left: '5%',
      right: '5%',
      bottom: '15%',
      top: '10%',
      containLabel: true,
    },
    xAxis: {
      type: 'category',
      data: chartData.map((item) => item.broadcast_date),
      axisLabel: {
        formatter: (value: string) => {
          const [year, month] = value.split('-');
          return `${year} ${month}`;
        },
        color: '#666',
        rotate: 45,
        margin: 15,
        interval: 'auto',
      },
      axisLine: {
        lineStyle: {
          color: '#ddd',
        },
      },
      axisTick: {
        show: true,
        alignWithLabel: true,
      },
    },
    yAxis: {
      type: 'value',
      minInterval: 1,
      axisLabel: {
        color: '#666',
      },
      axisLine: {
        lineStyle: {
          color: '#ddd',
        },
      },
      splitLine: {
        lineStyle: {
          color: '#f0f0f0',
        },
      },
    },
    series: [
      {
        data: chartData.map((item) => item.count),
        type: 'line',
        smooth: true,
        symbol: 'circle',
        symbolSize: 8,
        lineStyle: {
          width: 3,
          color: '#3b82f6',
        },
        itemStyle: {
          color: '#3b82f6',
          borderColor: '#fff',
          borderWidth: 2,
        },
        areaStyle: {
          color: {
            type: 'linear',
            x: 0,
            y: 0,
            x2: 0,
            y2: 1,
            colorStops: [
              {
                offset: 0,
                color: 'rgba(59, 130, 246, 0.3)',
              },
              {
                offset: 1,
                color: 'rgba(59, 130, 246, 0.1)',
              },
            ],
          },
        },
      },
    ],
  };

  const artistChartOption = {
    ...chartOption,
    grid: {
      ...chartOption.grid,
      top: '5%',
    },
    xAxis: {
      ...chartOption.xAxis,
      data: artistChartData.map((item) => item.broadcast_date),
    },
    series: [
      {
        ...chartOption.series[0],
        data: artistChartData.map((item) => item.count),
        lineStyle: {
          width: 3,
          color: '#10b981',
        },
        itemStyle: {
          color: '#10b981',
          borderColor: '#fff',
          borderWidth: 2,
        },
        areaStyle: {
          color: {
            type: 'linear',
            x: 0,
            y: 0,
            x2: 0,
            y2: 1,
            colorStops: [
              {
                offset: 0,
                color: 'rgba(16, 185, 129, 0.3)',
              },
              {
                offset: 1,
                color: 'rgba(16, 185, 129, 0.1)',
              },
            ],
          },
        },
      },
    ],
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl w-[95vw] sm:w-[90vw] md:w-[80vw] lg:w-[60vw] mx-auto !bg-gray-900 shadow-lg rounded-lg my-8 sm:my-12">
        <div className="absolute right-4 top-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            className="rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 h-10 w-10 outline-none focus:ring-0 bg-gray-800 dark:bg-gray-700"
          >
            <X className="h-6 w-6 text-white" />
          </Button>
        </div>
        <DialogHeader>
          <div className="flex items-start gap-4 pr-12">
            <div className="relative w-20 h-20 flex-shrink-0">
              {imageLoading && (
                <div className="absolute inset-0 flex items-center justify-center bg-gray-100 dark:bg-gray-800 rounded-full">
                  <div className="animate-spin rounded-full h-8 w-8 border-4 border-primary border-t-transparent" />
                </div>
              )}
              {!imageLoading && artistImage && (
                <img
                  src={artistImage}
                  alt={artist}
                  className="w-full h-full rounded-full object-cover shadow-md aspect-square"
                />
              )}
              {!imageLoading && !artistImage && (
                <div className="w-full h-full rounded-full bg-gradient-to-br from-gray-200 to-gray-300 dark:from-gray-700 dark:to-gray-800 flex items-center justify-center shadow-inner aspect-square">
                  <span className="text-gray-500 dark:text-gray-400 text-2xl font-bold">
                    {artist.charAt(0).toUpperCase()}
                  </span>
                </div>
              )}
            </div>
            <div className="flex flex-col min-w-0">
              <DialogTitle className="text-xl font-semibold text-white font-mono text-left break-words whitespace-normal overflow-visible">
                {title}
              </DialogTitle>
              <div className="flex items-center gap-2">
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-7 px-2 rounded-md bg-gray-800 hover:bg-gray-700 text-white border-gray-700 flex items-center gap-1.5"
                        onClick={(e) => {
                          e.stopPropagation();
                          window.location.href = `/artist/${encodeURIComponent(artist)}`;
                        }}
                        title={`View ${artist} details`}
                      >
                        <User className="h-3.5 w-3.5" />
                        <span className="text-xs">Profile</span>
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>View {artist} details</p>
                    </TooltipContent>
                  </Tooltip>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={(e) => {
                          e.stopPropagation();
                          window.open(
                            `https://www.youtube.com/results?search_query=${encodeURIComponent(
                              `${artist} ${title}`
                            )}`,
                            '_blank'
                          );
                        }}
                        className="text-muted-foreground hover:text-primary flex-shrink-0 h-7 w-7 p-0"
                      >
                        <Youtube className="h-4 w-4 text-[#FF0000]" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Search on YouTube</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
                <span className="text-sm text-gray-500 dark:text-gray-400 font-mono text-left truncate">
                  {artist}
                </span>
              </div>
            </div>
          </div>
        </DialogHeader>
        <div className="flex flex-col gap-4">
          <div className="h-[180px] w-full bg-transparent dark:bg-transparent rounded-lg p-4">
            <div className="flex items-center justify-between text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              <span className="font-mono">Song Play Count</span>
              <span className="font-mono text-primary">
                Total: {chartData.reduce((sum, item) => sum + item.count, 0)} plays
              </span>
            </div>
            {loading ? (
              <div className="flex items-center justify-center h-full">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
              </div>
            ) : error ? (
              <div className="flex items-center justify-center h-full text-red-500">{error}</div>
            ) : chartData.length === 0 ? (
              <div className="flex items-center justify-center h-full text-gray-500 dark:text-gray-400">
                No play records in the last year
              </div>
            ) : (
              <ReactECharts
                ref={chartRef}
                option={chartOption}
                style={{ height: '100%', width: '100%' }}
                opts={{ renderer: 'canvas' }}
                notMerge={true}
                lazyUpdate={true}
                theme="light"
              />
            )}
          </div>
          <div className="h-[250px] w-full bg-transparent dark:bg-transparent rounded-lg p-4">
            <div className="flex items-center justify-between text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              <span className="font-mono">Artist Total Play Count</span>
              <span className="font-mono text-emerald-500">
                Total: {artistChartData.reduce((sum, item) => sum + item.count, 0)} plays
              </span>
            </div>
            {loading ? (
              <div className="flex items-center justify-center h-full">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
              </div>
            ) : error ? (
              <div className="flex items-center justify-center h-full text-red-500">{error}</div>
            ) : artistChartData.length === 0 ? (
              <div className="flex items-center justify-center h-full text-gray-500 dark:text-gray-400">
                No play records in the last year
              </div>
            ) : (
              <ReactECharts
                ref={artistChartRef}
                option={artistChartOption}
                style={{ height: '100%', width: '100%' }}
                opts={{ renderer: 'canvas' }}
                notMerge={true}
                lazyUpdate={true}
                theme="light"
              />
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ChartModal;
